import type { ShopLanguageRoute } from "./types";

export const SHOP_STOREFRONT_ROUTE_SLUGS = {
    cs: { stores: "prodejny", sale: "vyprodej" },
    sk: { stores: "predajne", sale: "vypredaj" },
    en: { stores: "stores", sale: "sale" },
    de: { stores: "filialen", sale: "angebote" },
    es: { stores: "tiendas", sale: "rebajas" },
    pl: { stores: "sklepy", sale: "wyprzedaz" },
    hu: { stores: "uzletek", sale: "akcio" },
    ro: { stores: "magazine", sale: "reduceri" },
} as const;

export type StorefrontSpecialRoute = keyof (typeof SHOP_STOREFRONT_ROUTE_SLUGS)["en"];

function normalizedPath(path: string): string {
    const [pathname] = path.split(/[?#]/, 1);
    const withoutTrailingSlash = pathname.replace(/\/+$/, "");

    return withoutTrailingSlash === "" ? "/" : withoutTrailingSlash;
}

function normalizedSearch(search: string): string {
    const trimmed = search.trim();

    if (trimmed === "" || trimmed === "?") {
        return "";
    }

    return trimmed.startsWith("?") ? trimmed : `?${trimmed}`;
}

export function storefrontLanguageCode(
    languageCode: string | null | undefined,
): keyof typeof SHOP_STOREFRONT_ROUTE_SLUGS {
    const normalized = languageCode?.trim().toLowerCase().replace("_", "-").split("-", 1)[0] ?? "";

    return normalized in SHOP_STOREFRONT_ROUTE_SLUGS
        ? normalized as keyof typeof SHOP_STOREFRONT_ROUTE_SLUGS
        : "en";
}

export function storefrontLanguagePrefix(
    path: string,
    supportedLanguageCodes: Iterable<string> = Object.keys(SHOP_STOREFRONT_ROUTE_SLUGS),
): { languageCode: string; path: string } | null {
    const normalized = normalizedPath(path);
    const segments = normalized.split("/").filter(Boolean);
    const supported = new Set(Array.from(supportedLanguageCodes, (code) => code.trim().toLowerCase()));
    const languageCode = segments[0]?.toLowerCase() ?? "";

    if (!supported.has(languageCode)) {
        return null;
    }

    return {
        languageCode,
        path: segments.length > 1 ? `/${segments.slice(1).join("/")}` : "/",
    };
}

export function storefrontSpecialRouteHref(
    route: StorefrontSpecialRoute,
    languageCode: string | null | undefined,
): string {
    const code = storefrontLanguageCode(languageCode);

    return `/${code}/${SHOP_STOREFRONT_ROUTE_SLUGS[code][route]}`;
}

export function storefrontSpecialRouteMatch(
    path: string,
): { route: StorefrontSpecialRoute; suffix: string } | null {
    const normalized = storefrontLanguagePrefix(path)?.path ?? normalizedPath(path);

    for (const routes of Object.values(SHOP_STOREFRONT_ROUTE_SLUGS)) {
        for (const route of ["stores", "sale"] as const) {
            const prefix = `/${routes[route]}`;

            if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
                return { route, suffix: normalized.slice(prefix.length) };
            }
        }
    }

    return null;
}

export function localizedStorefrontHref(
    href: string,
    languageCode: string | null | undefined,
): string {
    const trimmed = href.trim();

    if (
        trimmed === ""
        || trimmed === "#"
        || trimmed.startsWith("#")
        || trimmed.startsWith("//")
        || /^(?:mailto|tel|sms|javascript):/i.test(trimmed)
    ) {
        return href;
    }

    let url: URL;

    try {
        url = new URL(trimmed, "https://shop.local");
    } catch {
        return href;
    }

    if (url.origin !== "https://shop.local") {
        return href;
    }

    url.searchParams.delete("language");
    const code = storefrontLanguageCode(languageCode);
    const unprefixedPath = storefrontLanguagePrefix(url.pathname)?.path ?? normalizedPath(url.pathname);
    const specialRoute = storefrontSpecialRouteMatch(unprefixedPath);
    const localizedPath = specialRoute
        ? `/${SHOP_STOREFRONT_ROUTE_SLUGS[code][specialRoute.route]}${specialRoute.suffix}`
        : unprefixedPath;
    const prefixedPath = localizedPath === "/" ? `/${code}` : `/${code}${localizedPath}`;

    return `${prefixedPath}${url.search}${url.hash}`;
}

export function hrefForStorefrontLanguage(
    currentPath: string,
    search: string,
    hash: string,
    languageCode: string,
    languageRoutes: ShopLanguageRoute[] = [],
    canonicalHref: string | null | undefined = null,
): string {
    const code = languageCode.trim().toLowerCase();
    const params = new URLSearchParams(normalizedSearch(search));
    const resourceRoute = languageRoutes.find((route) => route.languageCode.toLowerCase() === code);
    const currentNormalizedPath = normalizedPath(currentPath);
    const canonicalPath = canonicalHref ? normalizedPath(canonicalHref) : null;
    const routeSuffix = canonicalPath !== null && currentNormalizedPath.startsWith(canonicalPath)
        ? currentNormalizedPath.slice(canonicalPath.length)
        : "";
    const preservedRouteSuffix = /^(?:[123]|-dokoncena)$/.test(routeSuffix) ? routeSuffix : "";
    const localizedPath = resourceRoute?.href
        ? `${normalizedPath(resourceRoute.href)}${preservedRouteSuffix}`
        : languageRoutes.length > 0
            ? `/${code}`
            : normalizedPath(localizedStorefrontHref(currentPath, code));

    params.delete("language");
    const serialized = params.toString();

    return `${localizedPath}${serialized === "" ? "" : `?${serialized}`}${hash}`;
}
